import { Fragment, useRef, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { Loader2, Save, X } from "lucide-react";
import { useActionLoad, useRecord } from "../react/queries/useAction";
import { useRuntime } from "../react/runtime/RuntimeContext";
import { RecordProvider, useFormStateContext } from "../react/form/RecordProvider";
import { FormView } from "./FormView";
import { ClientActionPage, type ClientActionPageHandle } from "./ClientActionPage";
import { useDialogActionStack, type DialogActionFrame } from "./DialogActionStack";
import type { Json, RecordData } from "../core";

// DialogActionHost — renders every `target=dialog` action overlay
// currently on the global stack (see `DialogActionStack`).
//
// Each frame becomes a Radix Dialog wrapping a `<FormView>` for the
// frame's (actionSlug, recordRef). Nested dialogs stack via the dialog
// stack provider; closing the top one pops the stack (Escape, X button,
// or programmatic close). The underlying workspace URL never changes.
//
// Today's host only supports `viewMode=form`; list / kanban inside a
// dialog isn't a current need and would require a different render
// shell.

export function DialogActionHost() {
    const { stack, closeAt } = useDialogActionStack();
    if (stack.length === 0) return null;
    return (
        <Fragment>
            {stack.map((frame, idx) => (
                <DialogActionFrameView
                    key={`${frame.actionSlug}::${frame.recordRef ?? "new"}::${idx}`}
                    frame={frame}
                    index={idx}
                    onClose={() => closeAt(idx)}
                />
            ))}
        </Fragment>
    );
}

function DialogActionFrameView({
    frame,
    index,
    onClose,
}: {
    frame: DialogActionFrame;
    index: number;
    onClose: () => void;
}) {
    const runtime = useRuntime();
    const actionQ = useActionLoad(frame.actionSlug);
    const recordRef = frame.recordRef;
    const recordQ = useRecord(frame.actionSlug, recordRef);

    const action = actionQ.data?.action;
    const formPlan = actionQ.data?.formPlan ?? null;
    const fieldDefs = actionQ.data?.fieldDefs ?? [];
    const viewFieldNames = actionQ.data?.viewFieldNames?.form ?? null;
    const modelKey = action?.modelKey ?? "";
    // titleOverride lets the registered client component replace the
    // static `ir.action.name` with a record-specific string. Component
    // sets via `useClientActionTitle(...)`; null = fall back to action.name.
    const [titleOverride, setTitleOverride] = useState<string | null>(null);
    const title = titleOverride ?? action?.name ?? "";
    // `clientComponent` set ⇒ action_type="client" — mount the registered
    // React component instead of the window-action FormView. Mirrors the
    // ActionManager branch so any consumer (Settings, Storage Drive,
    // Approval Inbox, …) can opt into dialog presentation by flipping
    // `target="dialog"` on its ir.action seed; the host figures out the
    // right surface from there.
    const isClientAction = Boolean(action?.clientComponent);

    // A new-record dialog (recordRef absent or "new") has no record to fetch —
    // a record-bound server action (Enh 10) opens its wizard in create mode, so
    // it is ready as soon as the action itself loads. Only an existing-record
    // dialog waits on the record query. (The RecordProvider below already keys
    // isNew off this same condition; the readiness gate must agree, or a
    // create-mode dialog hangs on "Loading…" forever.)
    const isNewRecord = !recordRef || recordRef === "new";
    const isLoading = actionQ.isLoading || (!isNewRecord && recordQ.isLoading);
    // Client actions are ready once the action's loaded — they don't need
    // a record fetch (param is forwarded as-is to the registered component).
    const isReady = isClientAction
        ? Boolean(actionQ.data)
        : Boolean(actionQ.data) && (isNewRecord || recordQ.data !== undefined);

    // Each open dialog gets +20 z-index per stack depth so nested dialogs
    // sit above their parent's content. Matches the depth-based offset
    // RelationListView uses for its own popup chain (base 200 / 201).
    const overlayZ = 200 + 20 * index;
    const contentZ = 201 + 20 * index;

    // Persistence — exactly the same write path ActionManager uses, so
    // saves from a dialog hit the same `ede.update` route + service
    // refetch dance that a full-page form save would.
    const onCreate = async (values: Record<string, Json>) => {
        const result = await runtime.services.action.createRecord(modelKey, values);
        // Notify the opener of a successful save (e.g. self-password-change →
        // logout). Defensive — a handler error must not fail the save.
        try {
            frame.onSaved?.(values);
        } catch {
            /* ignore */
        }
        return result;
    };
    const onUpdate = (uuid: string, values: Record<string, Json>) =>
        runtime.services.action.updateRecord(modelKey, uuid, values);

    // Client-action close interception. The mounted ClientActionPage
    // exposes a `tryClose()` imperative handle that runs the registered
    // onClose veto chain (themed confirm + reason text). We hold a
    // ref to it and wrap the dialog's close paths (X button, Escape
    // key, onOpenChange) so a registered onClose has a chance to
    // veto BEFORE the frame pops. For window actions the ref stays
    // null and the X / Escape close the dialog directly — no veto
    // applies (the form's own dirty state is handled by FormSheet).
    const clientPageRef = useRef<ClientActionPageHandle>(null);
    const handleCloseRequest = async () => {
        const handle = clientPageRef.current;
        if (!handle) {
            onClose();
            return;
        }
        const ok = await handle.tryClose();
        if (ok) onClose();
    };

    return (
        <Dialog.Root
            open
            onOpenChange={(open) => {
                // Radix calls this on Escape, X click, and outside-click.
                // We always intercept so the client-action veto runs
                // before the frame pops. For window actions the handle
                // is null and `handleCloseRequest` falls through to a
                // direct onClose() — no change to existing behaviour.
                if (!open) void handleCloseRequest();
            }}
        >
            <Dialog.Portal>
                <Dialog.Overlay className="theme-dialog__overlay" style={{ zIndex: overlayZ }} />
                <Dialog.Content
                    className={`relation-dialog ${isClientAction ? "relation-dialog--client" : ""}`.trim()}
                    style={{ zIndex: contentZ }}
                    onPointerDownOutside={(e) => e.preventDefault()}
                    onInteractOutside={(e) => e.preventDefault()}
                >
                    <header className="relation-dialog__head">
                        <Dialog.Title className="relation-dialog__title">{title || "Loading…"}</Dialog.Title>
                        {/* Manual close button (was <Dialog.Close>) — routes
                            through handleCloseRequest so the client-action
                            veto chain runs. Dialog.Close would fire
                            onOpenChange synchronously and we'd lose the
                            async veto window. */}
                        <button
                            type="button"
                            className="theme-dialog__close"
                            aria-label="Close"
                            onClick={() => void handleCloseRequest()}
                        >
                            <X size={16} />
                        </button>
                    </header>
                    <Dialog.Description className="theme-dialog__sr-only">{title}</Dialog.Description>
                    {!isReady ? (
                        <div className="relation-dialog__body relation-dialog__body--loading">
                            <Loader2 size={20} className="dialog-action__spinner" />
                            <span>Loading…</span>
                        </div>
                    ) : isClientAction ? (
                        // Client-action branch — mount via ClientActionPage
                        // (the unified host: registry lookup + param
                        // validation + lifecycle context + Suspense +
                        // permission gate). The dialog wraps it as a
                        // presentation surface only; no <RecordProvider>,
                        // no <FormView>, no DialogActionFooter — those
                        // are window-action concerns. Passing `mountedAs="dialog"`
                        // + `onRequestClose` enables the lifecycle veto:
                        // a registered onClose can return canClose=false
                        // and the host shows the themed discard-confirm
                        // before popping the frame.
                        <div className="relation-dialog__body relation-dialog__body--client">
                            <ClientActionPage
                                ref={clientPageRef}
                                action={action!}
                                param={recordRef ?? undefined}
                                mountedAs="dialog"
                                onRequestClose={onClose}
                                onTitleChange={setTitleOverride}
                            />
                        </div>
                    ) : (
                        <RecordProvider
                            key={recordRef ?? "new"}
                            // Enh 10 — a record-bound action opens a new-record
                            // wizard pre-seeded from the active record (e.g.
                            // user_id). frame.defaults carries those seed values.
                            record={recordQ.data ?? (frame.defaults ? ({ id: "", ...frame.defaults } as RecordData) : null)}
                            isNew={!recordRef || recordRef === "new"}
                            fieldDefs={fieldDefs}
                            viewFieldNames={viewFieldNames}
                            recordRef={recordRef === "new" ? undefined : recordRef}
                            initialIsEditing
                            formElements={formPlan?.sheet ?? undefined}
                            onCreate={onCreate}
                            onUpdate={onUpdate}
                        >
                            <div className="relation-dialog__body">
                                <FormView
                                    title={title}
                                    recordRef={recordRef ?? "new"}
                                    modelKey={modelKey}
                                    actionSlug={frame.actionSlug}
                                    fieldDefs={fieldDefs}
                                    isLoading={isLoading}
                                    formPlan={formPlan}
                                />
                            </div>
                            <DialogActionFooter onClose={onClose} />
                        </RecordProvider>
                    )}
                </Dialog.Content>
            </Dialog.Portal>
        </Dialog.Root>
    );
}

function DialogActionFooter({ onClose }: { onClose: () => void }) {
    const form = useFormStateContext();
    const handleSave = async () => {
        const result = await form.onSave();
        if (result.ok) onClose();
    };
    return (
        <footer className="relation-dialog__foot">
            <button
                type="button"
                className="btn relation-dialog__save"
                onClick={() => void handleSave()}
                disabled={form.isSaving || (!form.isNew && !form.isDirty)}
            >
                {form.isSaving ? (
                    <Loader2 size={14} className="dialog-action__spinner" />
                ) : (
                    <Save size={14} />
                )}
                <span>{form.isSaving ? "Saving…" : "Save"}</span>
            </button>
            <button
                type="button"
                className="btn relation-dialog__cancel"
                onClick={onClose}
                disabled={form.isSaving}
            >
                Cancel
            </button>
        </footer>
    );
}
